// Cue presentation and the metronome, both of which have to straddle the two
// clocks. The study layer is part of the core, not an interface, so it is
// allowed to import Tone directly -- the "interfaces never touch audio" rule
// is about interfaces. See docs/timing.md.

import * as Tone from 'tone'
import { sampleClockPair, audioToPerf } from '../clock.js'

// How far ahead a cue is scheduled. Needs to exceed the context lookAhead so
// the audio and the Draw callback both have room, but stay short enough that
// the round does not feel sluggish.
const CUE_LEAD_S = 0.15

// Present one cue. Resolves with the timing facts of the cue itself, which the
// trial row needs before it can interpret any response.
export function scheduleCue({ engine, sound, cueType, displayPeriodMs, onFlash }) {
  const pair = sampleClockPair()
  const audioTime = Tone.immediate() + CUE_LEAD_S

  if (cueType === 'audio' || cueType === 'both') engine.trigger(sound, audioTime)

  return new Promise((resolve) => {
    let settled = false
    const report = (frameMs, painted) => {
      if (settled) return
      settled = true
      clearTimeout(fallback)
      resolve({
        cue_painted: painted,
        cue_frame_ms: frameMs,
        // Pixels reach the panel at the next vsync at the earliest. Anything
        // beyond that in the display pipeline is not observable from JS.
        cue_visual_ms: frameMs + displayPeriodMs,
        cue_audio_s: audioTime,
        cue_audio_ms: audioToPerf(audioTime, pair),
        cue_av_gap_ms: audioToPerf(audioTime, pair) - (frameMs + displayPeriodMs),
        map_ctx_time_s: pair.ctxTime,
        map_perf_ms: pair.perfMs,
        ctx_perf_offset_ms: pair.perfMs - pair.ctxTime * 1000,
        clock_map_method: pair.method,
        map_consistency_ms: pair.consistencyMs ?? '',
      })
    }

    // A backgrounded tab stops firing rAF, which would otherwise leave the
    // round waiting forever. Fall back to the audio clock and mark the row so
    // the analysis can see the cue time was estimated, not observed.
    const fallback = setTimeout(() => {
      report(audioToPerf(audioTime, pair) - displayPeriodMs, false)
    }, (CUE_LEAD_S * 1000) + 1000)

    // Draw runs the callback on the rAF frame nearest the audio time, so its
    // rAF timestamp is the frame on which the cue was painted.
    Tone.getDraw().schedule(() => {
      // rAF timestamp of the current frame; Draw itself does not hand it over.
      requestAnimationFrame((frameMs) => {
        if (cueType === 'visual' || cueType === 'both') onFlash?.()
        report(frameMs, true)
      })
    }, audioTime)
  })
}

// Records when each click actually happened, in both clocks, so a response can
// be compared against the nearest one. Scheduling the clicks here rather than
// reading the Transport's position means the click times are known exactly
// instead of reconstructed.
export function createMetronome({ engine, sound = 'hat' }) {
  const clicks = []          // { audioS, perfMs, index }
  let eventId = null
  let index = 0

  return {
    start() {
      if (eventId !== null) return
      const transport = Tone.getTransport()
      eventId = transport.scheduleRepeat((time) => {
        engine.trigger(sound, time)
        const pair = sampleClockPair()
        clicks.push({ audioS: time, perfMs: audioToPerf(time, pair), index: index++ })
        if (clicks.length > 64) clicks.shift()
      }, '4n')
      if (!engine.playing) transport.start()
    },

    stop() {
      if (eventId !== null) { Tone.getTransport().clear(eventId); eventId = null }
    },

    // Signed error against the nearest click, wrapped to half a beat either
    // side. Negative is early, which is where humans naturally sit.
    asynchronyFor(perfMs, bpm) {
      if (clicks.length === 0) return { asynchrony_ms: '', beat_index: '', click_ms: '', beat_period_ms: '' }
      const period = 60000 / bpm
      let nearest = clicks[0]
      for (const c of clicks) {
        if (Math.abs(perfMs - c.perfMs) < Math.abs(perfMs - nearest.perfMs)) nearest = c
      }
      let async_ = perfMs - nearest.perfMs
      // wrap into (-period/2, +period/2]
      while (async_ > period / 2) async_ -= period
      while (async_ <= -period / 2) async_ += period
      return {
        asynchrony_ms: async_,
        beat_index: nearest.index,
        click_ms: nearest.perfMs,
        click_audio_s: nearest.audioS,
        beat_period_ms: period,
      }
    },

    get clicks() { return [...clicks] },
  }
}
